import { useMemo } from "react";
import { blobField, pickMotif, type Motif } from "../shared/motif";

// The morphing colour field the glass sits on.
//
// Lava lamp, not bubbles: the shapes squish and rotate and drift rather than
// float by. It is fixed behind everything and never takes a touch, so every
// screen gets the same light without any screen having to think about it.

const KEY = "blob_motif";

// One motif per app-open. Kept in sessionStorage rather than state so it
// survives navigation inside a visit and changes on the next one.
function motifForThisVisit(): Motif {
  try {
    const held = sessionStorage.getItem(KEY);
    if (held === "circles" || held === "pills" || held === "strokes") return held;
    const picked = pickMotif();
    sessionStorage.setItem(KEY, picked);
    return picked;
  } catch {
    // Private mode: still get a field, just a fresh one each render pass.
    return pickMotif();
  }
}

export function BlobField({ count = 8, className = "", speed = 1 }: {
  count?: number;
  className?: string;
  // Above 1 the field drifts quicker. The champion beat is the one place this
  // is allowed to be lively rather than calm.
  speed?: number;
}) {
  const blobs = useMemo(() => blobField(motifForThisVisit(), count), [count]);
  return (
    <div className={`blobfield ${className}`} aria-hidden="true">
      {blobs.map((b, i) => (
        <span
          key={i}
          className="blob"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            width: `${Math.round(b.width)}px`,
            height: `${Math.round(b.height)}px`,
            borderRadius: b.radius,
            background: `radial-gradient(circle at 38% 34%, rgba(255,255,255,0.6), rgba(${b.color},0.82))`,
            animationDuration: `${(b.duration / speed).toFixed(1)}s`,
            animationDelay: `${b.delay.toFixed(1)}s`,
            ["--spin" as any]: `${b.rotate.toFixed(0)}deg`,
          }}
        />
      ))}
    </div>
  );
}
